import { useId, useState } from 'react';
import type { Sistema, TenantResumen } from '../api';
import { nombreDeTenant } from '../tenants';

/**
 * La lista blanca de tenants de un sistema: a nombre de qué contribuyentes puede
 * facturar esta aplicación.
 *
 * # ES LO QUE ACOTA EL DAÑO DE UNA KEY FILTRADA
 *
 * Emitir es lista blanca **explícita**: sin fila en `sistemas_tenants` el
 * intento de emitir da `403 tenant_no_autorizado`. No es "todo lo que no esté
 * prohibido". Un sistema sin ningún tenant se autentica perfectamente y no puede
 * emitir para nadie — eso es válido y es el estado más seguro.
 *
 * # SE ELIGE POR NOMBRE, NUNCA ESCRIBIENDO UN ID
 *
 * La API se maneja con el id numérico de la fila de `tenants` (no el CUIT), pero
 * **acá no hay ningún campo donde escribir un número**: se elige de una lista que
 * viene de `GET /api/v1/consultas/tenants` y muestra razón social + CUIT.
 *
 * El motivo no es comodidad. Equivocarse en ese id es autorizar a una aplicación
 * a facturar a nombre del CUIT equivocado, y lo que salga de ahí es un
 * comprobante fiscal a nombre de otro contribuyente: no se borra, se corrige con
 * nota de crédito. Un `<input type="number">` convierte un error de tipeo en un
 * permiso mal dado que nadie va a notar hasta que aparezca la factura.
 *
 * # LOS YA AUTORIZADOS NO SE OFRECEN
 *
 * El selector muestra solo los que faltan. Autorizar dos veces es idempotente en
 * el backend, así que no rompería nada; se saca de la lista para que se vea de un
 * golpe qué falta, que es la pregunta que uno le hace a esta pantalla.
 */
export function ListaBlanca({
  sistema,
  tenants,
  ocupada,
  alAutorizar,
  alRevocar,
}: {
  sistema: Sistema;
  /** Todos los tenants visibles. Vacío mientras carga: ver el mensaje de abajo. */
  tenants: readonly TenantResumen[];
  ocupada: boolean;
  alAutorizar: (tenantId: number) => void;
  alRevocar: (tenantId: number) => void;
}) {
  const idSelector = useId();
  const [elegido, setElegido] = useState('');

  const indice = new Map(tenants.map((t) => [t.id, t]));
  const autorizados = [...sistema.tenantsAutorizados]
    // El contrato avisa que `tenantsAutorizados` es un Set y NO tiene orden
    // garantizado. Sin ordenar acá, la lista bailaría entre refrescos y sería
    // imposible ver de un vistazo si cambió algo.
    .map((id) => ({ id, tenant: indice.get(id) }))
    .sort((a, b) => etiqueta(a).localeCompare(etiqueta(b)));
  const disponibles = tenants.filter((t) => !sistema.tenantsAutorizados.includes(t.id));

  return (
    <div>
      <h3>Tenants que puede facturar {sistema.nombre}</h3>

      {autorizados.length === 0 ? (
        <p className="nota">
          Ninguno. El sistema se autentica pero no puede emitir para nadie: emitir es lista blanca,
          sin fila da 403.
        </p>
      ) : (
        <ul>
          {autorizados.map(({ id, tenant }) => (
            <li key={id}>
              {tenant ? nombreDeTenant(tenant) : `Tenant #${id} (ya no figura en la lista)`}{' '}
              <button
                type="button"
                aria-label={`Quitar ${tenant ? tenant.razonSocial : `el tenant #${id}`} de ${sistema.nombre}`}
                disabled={ocupada}
                onClick={() => alRevocar(id)}
              >
                Quitar
              </button>
              {tenant && !tenant.puedeEmitir ? (
                // Se autoriza igual: el permiso se configura antes de que ARCA
                // conteste la delegación. Pero conviene saber por qué, si el
                // sistema ya autorizado sigue recibiendo errores al emitir.
                <span className="nota"> — su delegación en ARCA todavía no está activa</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <p>
        <label htmlFor={idSelector}>Autorizar un tenant</label>
        <br />
        <select
          id={idSelector}
          value={elegido}
          disabled={ocupada || disponibles.length === 0}
          onChange={(e) => setElegido(e.target.value)}
        >
          <option value="">Elegí un tenant…</option>
          {disponibles.map((tenant) => (
            <option key={tenant.id} value={String(tenant.id)}>
              {nombreDeTenant(tenant)}
            </option>
          ))}
        </select>{' '}
        <button
          type="button"
          aria-label={`Autorizar el tenant elegido para ${sistema.nombre}`}
          disabled={ocupada || elegido === ''}
          onClick={() => {
            alAutorizar(Number(elegido));
            setElegido('');
          }}
        >
          Autorizar
        </button>
        <br />
        {tenants.length === 0 ? (
          <span className="nota">No hay tenants para elegir todavía.</span>
        ) : disponibles.length === 0 ? (
          <span className="nota">Ya están autorizados todos los tenants.</span>
        ) : (
          <span className="nota">
            Se elige por razón social y CUIT. Autorizar a nombre del contribuyente equivocado
            termina en un comprobante fiscal mal emitido.
          </span>
        )}
      </p>
    </div>
  );
}

/** Con qué texto se ordena una fila autorizada, exista o no el tenant. */
function etiqueta(fila: { id: number; tenant: TenantResumen | undefined }): string {
  return fila.tenant ? fila.tenant.razonSocial : `#${fila.id}`;
}
